import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner, Table, Badge, Card } from 'react-bootstrap';
import moment from 'moment';
import AsyncSelect from 'react-select/async';
import {
  appointmentService,
  locationService,
  userService,
  groupService,
  subscriptionService
} from '../../services/api';
import { getValidSubscriptions } from '../../helpers/subscriptionHelpers';
import { formatGroupOptionLabel } from '../../helpers/groupHelpers';
import AppointmentRollCallModal from './AppointmentRollCallModal';
import './AppointmentModal.css';

const AppointmentModal = ({
  show,
  onHide,
  appointmentId,
  initialDate,
  initialStartTime,
  initialEndTime,
  onSuccess
}) => {
  const isEditing = Boolean(appointmentId);

  // Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('15:00');
  const [endTime, setEndTime] = useState('16:00');
  const [locationId, setLocationId] = useState('');
  const [coachId, setCoachId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [selectedGroupOption, setSelectedGroupOption] = useState(null);
  const [status, setStatus] = useState('scheduled');
  const [seriesId, setSeriesId] = useState(null);

  // Recurrence
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatUntil, setRepeatUntil] = useState('');

  // Participants
  const [participants, setParticipants] = useState([]);

  // Options State
  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);

  // Async & Error State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflictReport, setConflictReport] = useState(null);
  const [forceOverride, setForceOverride] = useState(false);

  // RollCall Modal State
  const [showRollCallModal, setShowRollCallModal] = useState(false);
  const [appointmentDetail, setAppointmentDetail] = useState(null);

  // Search Debounce Refs
  const groupSearchTimeoutRef = useRef(null);
  const athleteSearchTimeoutRef = useRef(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (groupSearchTimeoutRef.current) clearTimeout(groupSearchTimeoutRef.current);
      if (athleteSearchTimeoutRef.current) clearTimeout(athleteSearchTimeoutRef.current);
    };
  }, []);

  // Lock check: block modifications if at least one participant was marked present in roll call
  const isLocked = Boolean(
    isEditing &&
    appointmentDetail?.participants?.some(p => p.isPresent === true)
  );

  // Load select options (locations, coaches) and apply defaults if single option
  useEffect(() => {
    if (!show) return;

    const loadOptions = async () => {
      try {
        const [locRes, coachRes] = await Promise.all([
          locationService.getAll(false),
          userService.getUsers({ role: 'tech', limit: 100 })
        ]);

        if (locRes.success) {
          const locList = Array.isArray(locRes.data) ? locRes.data : (locRes.data?.items || []);
          setLocations(locList);
          if (!appointmentId && locList.length === 1) {
            setLocationId(locList[0].id);
          }
        }
        if (coachRes.success) {
          const coachList = Array.isArray(coachRes.data)
            ? coachRes.data
            : (coachRes.data?.items || coachRes.data?.users || []);
          setCoaches(coachList);
          if (!appointmentId && coachList.length === 1) {
            setCoachId(coachList[0].id);
          }
        }
      } catch (err) {
        console.error('Error loading planner options:', err);
      }
    };

    loadOptions();
  }, [show, appointmentId]);

  // Load appointment details if editing
  const loadAppointment = useCallback(async () => {
    if (!appointmentId) {
      // Create mode defaults
      setTitle('');
      setDescription('');
      const baseDate = initialDate ? moment(initialDate) : moment();
      setDate(baseDate.format('YYYY-MM-DD'));
      setStartTime(initialStartTime || baseDate.format('HH:mm') || '15:00');
      setEndTime(initialEndTime || baseDate.clone().add(1, 'hour').format('HH:mm') || '16:00');
      setLocationId(locations.length === 1 ? locations[0].id : '');
      setCoachId(coaches.length === 1 ? coaches[0].id : '');
      setGroupId('');
      setSelectedGroupOption(null);
      setStatus('scheduled');
      setSeriesId(null);
      setRepeatWeekly(false);
      setRepeatUntil(baseDate.clone().add(1, 'month').format('YYYY-MM-DD'));
      setParticipants([]);
      setConflictReport(null);
      setForceOverride(false);
      return;
    }

    setLoading(true);
    setError('');
    const res = await appointmentService.getById(appointmentId);
    setLoading(false);

    if (res.success && res.data) {
      const data = res.data;
      setAppointmentDetail(data);
      setTitle(data.title);
      setDescription(data.description || '');
      setDate(moment(data.startTime).format('YYYY-MM-DD'));
      setStartTime(moment(data.startTime).format('HH:mm'));
      setEndTime(moment(data.endTime).format('HH:mm'));
      setLocationId(data.locationId);
      setCoachId(data.coachId);
      setGroupId(data.groupId || '');
      if (data.groupId) {
        setSelectedGroupOption({
          value: data.groupId,
          label: formatGroupOptionLabel(data.groupName || 'Gruppo', data.groupAcademicYear)
        });
      } else {
        setSelectedGroupOption(null);
      }
      setStatus(data.status);
      setSeriesId(data.seriesId);
      setRepeatWeekly(false);
      setParticipants(data.participants.map(p => ({
        userId: p.userId,
        userName: p.userName,
        billingMode: p.courseSubscriptionId ? 'subscription' : 'manual',
        courseSubscriptionId: p.courseSubscriptionId || '',
        price: p.price ?? 20,
        isPaid: p.isPaid ?? false,
        subscriptionName: p.subscriptionName,
        remainingEntries: p.remainingEntries,
        isPresent: p.isPresent
      })));
      setConflictReport(null);
      setForceOverride(false);
    } else {
      const errText = typeof res.message === 'string'
        ? res.message
        : (res.message?.message || 'Errore nel caricamento del dettaglio');
      setError(errText);
    }
  }, [appointmentId, initialDate, initialStartTime, initialEndTime, locations, coaches]);

  useEffect(() => {
    if (show) {
      loadAppointment();
    }
  }, [show, loadAppointment]);

  // Load groups asynchronously with debounce
  const loadGroupsAsync = (inputValue) => {
    return new Promise((resolve) => {
      if (groupSearchTimeoutRef.current) {
        clearTimeout(groupSearchTimeoutRef.current);
      }

      groupSearchTimeoutRef.current = setTimeout(async () => {
        try {
          if (!inputValue || inputValue.length < 2) {
            resolve([]);
            return;
          }

          const response = await groupService.getGroups({
            search: inputValue,
            page: 1,
            limit: 50
          });

          if (response.success) {
            const items = Array.isArray(response.data)
              ? response.data
              : (response.data?.items || response.data?.groups || []);

            const options = items.map(g => ({
              value: g.id,
              label: formatGroupOptionLabel(g),
              group: g
            }));

            resolve(options);
          } else {
            resolve([]);
          }
        } catch (err) {
          console.error('Error loading groups:', err);
          resolve([]);
        }
      }, 300);
    });
  };

  // Group selection change: populates or clears participants
  const handleGroupChange = async (selectedOption) => {
    setSelectedGroupOption(selectedOption);

    if (!selectedOption) {
      setGroupId('');
      setParticipants([]);
      return;
    }

    const newGroupId = selectedOption.value;
    setGroupId(newGroupId);

    try {
      const res = await groupService.getGroup(newGroupId);
      if (res.success && res.data) {
        const groupData = res.data;
        const appDate = moment(date || new Date()).toDate();
        const validSubs = getValidSubscriptions(groupData.courseSubscriptions || [], appDate);

        // Deduplicate users if an athlete has multiple subscriptions
        const userSubsMap = new Map();
        for (const sub of validSubs) {
          if (sub?.user?.id && !userSubsMap.has(sub.user.id)) {
            userSubsMap.set(sub.user.id, sub);
          }
        }

        const newParticipants = Array.from(userSubsMap.values()).map(sub => ({
          userId: sub.user.id,
          userName: `${sub.user.firstName} ${sub.user.lastName}`.trim(),
          billingMode: 'subscription',
          courseSubscriptionId: sub.id,
          subscriptionName: sub.offer?.name || groupData.course?.name || 'Abbonamento',
          remainingEntries: sub.remainingEntries ?? null,
          price: 20,
          isPaid: false
        }));

        setParticipants(newParticipants);
      }
    } catch (err) {
      console.error('Error loading group participants:', err);
    }
  };

  // Load athletes asynchronously with debounce
  const loadAthletesAsync = (inputValue) => {
    return new Promise((resolve) => {
      if (athleteSearchTimeoutRef.current) {
        clearTimeout(athleteSearchTimeoutRef.current);
      }

      athleteSearchTimeoutRef.current = setTimeout(async () => {
        try {
          if (!inputValue || inputValue.length < 2) {
            resolve([]);
            return;
          }

          const response = await userService.getUsers({
            role: 'athlete',
            search: inputValue,
            page: 1,
            limit: 50
          });

          if (response.success) {
            const items = Array.isArray(response.data)
              ? response.data
              : (response.data?.items || response.data?.users || []);

            const currentParticipantIds = participants.map(p => p.userId);
            const filtered = items.filter(u => !currentParticipantIds.includes(u.id));

            const options = filtered.map(u => ({
              value: u.id,
              label: `${u.firstName} ${u.lastName} - ${u.email}`,
              user: u
            }));

            resolve(options);
          } else {
            resolve([]);
          }
        } catch (err) {
          console.error('Error loading athletes:', err);
          resolve([]);
        }
      }, 300);
    });
  };

  // Handle athlete selection from AsyncSelect (immediate addition)
  const handleSelectAthlete = async (selectedOption) => {
    if (!selectedOption || !selectedOption.user) return;
    const athleteUser = selectedOption.user;

    if (participants.some(p => p.userId === athleteUser.id)) {
      setError('L\'atleta è già presente nella lista partecipanti');
      return;
    }

    const athleteName = `${athleteUser.firstName} ${athleteUser.lastName}`.trim();

    let userSubs = [];
    try {
      const subRes = await subscriptionService.getSubscriptions({ userId: athleteUser.id });
      if (subRes.success) {
        userSubs = subRes.data?.items || subRes.data || [];
      }
    } catch (e) {
      console.warn('Could not fetch subscriptions for athlete', e);
    }

    const hasActiveSub = userSubs.length > 0;
    const defaultSub = hasActiveSub ? userSubs[0] : null;

    setParticipants(prev => [
      ...prev,
      {
        userId: athleteUser.id,
        userName: athleteName,
        billingMode: hasActiveSub ? 'subscription' : 'manual',
        courseSubscriptionId: defaultSub ? defaultSub.id : '',
        subscriptionName: defaultSub ? (defaultSub.offer?.name || defaultSub.course?.name) : null,
        remainingEntries: defaultSub?.remainingEntries ?? null,
        price: 20,
        isPaid: false
      }
    ]);
  };

  const handleRemoveParticipant = (index) => {
    setParticipants(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleUpdateParticipant = (index, field, value) => {
    setParticipants(prev => {
      const copy = [...prev];
      copy[index][field] = value;
      return copy;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Il titolo è obbligatorio');
      return;
    }
    if (!locationId) {
      setError('Seleziona una stanza/location');
      return;
    }
    if (!coachId) {
      setError('Seleziona un coach');
      return;
    }

    const startDateTime = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
    const endDateTime = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm').toDate();

    if (startDateTime >= endDateTime) {
      setError('L\'orario di fine deve essere successivo all\'orario di inizio');
      return;
    }

    const mappedParticipants = participants.map(p => ({
      userId: p.userId,
      courseSubscriptionId: p.billingMode === 'subscription' && p.courseSubscriptionId ? p.courseSubscriptionId : null,
      price: p.billingMode === 'manual' ? parseFloat(p.price) || 0 : null,
      isPaid: p.billingMode === 'manual' ? Boolean(p.isPaid) : false,
      paymentDate: p.billingMode === 'manual' && p.isPaid ? new Date().toISOString() : null
    }));

    setSaving(true);

    let res;
    if (isEditing) {
      res = await appointmentService.update(appointmentId, {
        title: title.trim(),
        description: description.trim() || null,
        startTime: startDateTime,
        endTime: endDateTime,
        locationId,
        coachId,
        groupId: groupId || null,
        status,
        allowConflicts: forceOverride,
        participants: mappedParticipants
      });
    } else {
      res = await appointmentService.create({
        title: title.trim(),
        description: description.trim() || null,
        startTime: startDateTime,
        endTime: endDateTime,
        locationId,
        coachId,
        groupId: groupId || null,
        allowConflicts: forceOverride,
        recurrence: repeatWeekly && repeatUntil ? {
          repeatWeekly: true,
          repeatUntil: moment(repeatUntil, 'YYYY-MM-DD').endOf('day').toDate()
        } : undefined,
        participants: mappedParticipants
      });
    }

    setSaving(false);

    if (res.success) {
      if (onSuccess) onSuccess();
      onHide();
    } else if (res.isConflict) {
      setConflictReport(res.conflicts);
    } else {
      const errText = typeof res.message === 'string'
        ? res.message
        : (res.message?.message || 'Errore durante il salvataggio');
      setError(errText);
    }
  };

  const handleDelete = async (deleteEntireSeries = false) => {
    const confirmMsg = deleteEntireSeries
      ? 'Sei sicuro di voler eliminare l\'intera serie ricorrente di questo appuntamento?'
      : 'Sei sicuro di voler eliminare questo appuntamento?';

    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    const res = await appointmentService.delete(appointmentId, deleteEntireSeries);
    setSaving(false);

    if (res.success) {
      if (onSuccess) onSuccess();
      onHide();
    } else {
      setError(res.message || 'Errore durante l\'eliminazione');
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title className="fs-5">
            <i className={`fas ${isEditing ? 'fa-calendar-check' : 'fa-calendar-plus'} text-primary me-2`}></i>
            {isEditing ? 'Dettaglio / Modifica Appuntamento' : 'Nuovo Appuntamento'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted small">Caricamento appuntamento...</p>
              </div>
            ) : (
              <>
                {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

                {/* Locked appointment alert if attendees are marked present */}
                {isLocked && (
                  <Alert variant="warning" className="border-warning shadow-sm mb-3">
                    <i className="fas fa-lock me-2"></i>
                    <strong>Modifiche bloccate:</strong> L&apos;appello per questo appuntamento è già stato effettuato e risultano partecipanti registrati come presenti. Non è possibile modificare i dettagli o eliminare l&apos;evento.
                  </Alert>
                )}

                {/* Conflict Warning Alert */}
                {conflictReport && conflictReport.hasConflicts && (
                  <Alert variant="warning" className="border-warning shadow-sm">
                    <h6 className="fw-bold text-danger mb-2">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Rilevati Conflitti di Pianificazione!
                    </h6>
                    <ul className="mb-2 ps-3 small">
                      {conflictReport.coachConflicts?.map((c, i) => (
                        <li key={`coach-${i}`}>
                          <strong>Coach {c.coachName}:</strong> già occupato in &quot;{c.title}&quot; ({moment(c.startTime).format('HH:mm')} - {moment(c.endTime).format('HH:mm')})
                        </li>
                      ))}
                      {conflictReport.locationConflicts?.map((l, i) => (
                        <li key={`loc-${i}`}>
                          <strong>Location {l.locationName}:</strong> già occupata da &quot;{l.title}&quot; ({moment(l.startTime).format('HH:mm')} - {moment(l.endTime).format('HH:mm')})
                        </li>
                      ))}
                      {conflictReport.athleteConflicts?.map((a, i) => (
                        <li key={`ath-${i}`}>
                          <strong>Atleta {a.userName}:</strong> già registrato in &quot;{a.title}&quot; ({moment(a.startTime).format('HH:mm')} - {moment(a.endTime).format('HH:mm')})
                        </li>
                      ))}
                    </ul>
                    <Form.Check
                      type="checkbox"
                      id="override-conflicts"
                      label={<strong>Forza salvataggio ignorando i conflitti (Override Amministratore)</strong>}
                      checked={forceOverride}
                      onChange={(e) => setForceOverride(e.target.checked)}
                      className="mt-2 text-dark"
                      disabled={isLocked}
                    />
                  </Alert>
                )}

                <Row className="g-3 mb-3">
                  <Col md={8}>
                    <Form.Group controlId="appointment-title">
                      <Form.Label className="fw-semibold small">Titolo Lezione / Appuntamento *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Es. Acrobatica Avanzata, Lezione Trampolino..."
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        disabled={saving || isLocked}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="appointment-status">
                      <Form.Label className="fw-semibold small">Stato</Form.Label>
                      <Form.Select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        disabled={saving || isLocked}
                      >
                        <option value="scheduled">In Programma</option>
                        <option value="completed">Completato</option>
                        <option value="cancelled">Annullato</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-2 g-sm-3 mb-3">
                  <Col xs={12} sm={4}>
                    <Form.Group controlId="appointment-date">
                      <Form.Label className="fw-semibold small">Data *</Form.Label>
                      <Form.Control
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                        disabled={saving || isLocked}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} sm={4}>
                    <Form.Group controlId="appointment-start-time">
                      <Form.Label className="fw-semibold small">Ora Inizio *</Form.Label>
                      <Form.Control
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        required
                        disabled={saving || isLocked}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} sm={4}>
                    <Form.Group controlId="appointment-end-time">
                      <Form.Label className="fw-semibold small">Ora Fine *</Form.Label>
                      <Form.Control
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        required
                        disabled={saving || isLocked}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-2 g-sm-3 mb-3">
                  <Col xs={12} sm={6} md={4}>
                    <Form.Group controlId="appointment-location">
                      <Form.Label className="fw-semibold small">Stanza / Spazio (Location) *</Form.Label>
                      <Form.Select
                        value={locationId}
                        onChange={(e) => setLocationId(e.target.value)}
                        required
                        disabled={saving || isLocked}
                      >
                        <option value="">-- Seleziona stanza --</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4}>
                    <Form.Group controlId="appointment-coach">
                      <Form.Label className="fw-semibold small">Coach / Istruttore *</Form.Label>
                      <Form.Select
                        value={coachId}
                        onChange={(e) => setCoachId(e.target.value)}
                        required
                        disabled={saving || isLocked}
                      >
                        <option value="">-- Seleziona coach --</option>
                        {coaches.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.firstName} {c.lastName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Group controlId="appointment-group">
                      <Form.Label className="fw-semibold small">Gruppo Corso (Opzionale)</Form.Label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadGroupsAsync}
                        onChange={handleGroupChange}
                        value={selectedGroupOption}
                        placeholder="Cerca gruppo per nome..."
                        noOptionsMessage={({ inputValue }) =>
                          !inputValue || inputValue.length < 2
                            ? "Digita almeno 2 caratteri per cercare..."
                            : "Nessun gruppo trovato"
                        }
                        loadingMessage={() => "Ricerca in corso..."}
                        isClearable
                        isDisabled={saving || isLocked}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: '38px',
                            fontSize: '0.875rem'
                          })
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Recurrence generator (only when creating) */}
                {!isEditing && (
                  <Card className="mb-3 border-light bg-light p-3">
                    <Form.Check
                      type="switch"
                      id="repeat-weekly-switch"
                      label="Ripeti settimanalmente questo appuntamento"
                      checked={repeatWeekly}
                      onChange={(e) => setRepeatWeekly(e.target.checked)}
                      disabled={saving || isLocked}
                      className="fw-semibold small mb-2"
                    />
                    {repeatWeekly && (
                      <Row className="align-items-center g-2 mt-1">
                        <Col sm={4}>
                          <Form.Label className="small mb-0">Ripeti ogni settimana fino al:</Form.Label>
                        </Col>
                        <Col sm={5}>
                          <Form.Control
                            type="date"
                            size="sm"
                            value={repeatUntil}
                            onChange={(e) => setRepeatUntil(e.target.value)}
                            min={date}
                            required={repeatWeekly}
                            disabled={saving || isLocked}
                          />
                        </Col>
                      </Row>
                    )}
                  </Card>
                )}

                {/* Participants Management */}
                <Card className="mb-3 border">
                  <Card.Header className="bg-white py-2 d-flex justify-content-between align-items-center">
                    <span className="fw-bold small text-primary">
                      <i className="fas fa-users me-1"></i>
                      Partecipanti ({participants.length})
                    </span>
                  </Card.Header>
                  <Card.Body className="p-3">
                    <Row className="g-2 mb-3">
                      <Col sm={12}>
                        <AsyncSelect
                          cacheOptions
                          defaultOptions={false}
                          loadOptions={loadAthletesAsync}
                          onChange={handleSelectAthlete}
                          value={null}
                          placeholder="Cerca atleta per nome o email..."
                          noOptionsMessage={({ inputValue }) =>
                            !inputValue || inputValue.length < 2
                              ? "Digita almeno 2 caratteri per cercare..."
                              : "Nessun atleta trovato"
                          }
                          loadingMessage={() => "Ricerca in corso..."}
                          isClearable
                          isDisabled={saving || isLocked}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px',
                              fontSize: '0.875rem'
                            })
                          }}
                        />
                      </Col>
                    </Row>

                    {participants.length === 0 ? (
                      <div className="text-center py-2 text-muted small">
                        Nessun atleta aggiunto all&apos;appuntamento.
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table size="sm" hover className="appointment-participants-table align-middle mb-0">
                          <thead className="table-light small">
                            <tr>
                              <th>Atleta</th>
                              <th>Modalità Quota</th>
                              <th style={{ width: '130px' }}>Dettagli Quota</th>
                              <th style={{ width: '40px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {participants.map((p, idx) => (
                              <tr key={p.userId}>
                                <td className="participant-col-name fw-semibold small">
                                  <i className="fas fa-user text-secondary me-2 d-inline d-md-none"></i>
                                  {p.userName}
                                </td>
                                <td className="participant-col-mode">
                                  <span className="mobile-col-label">Modalità Quota</span>
                                  <Form.Select
                                    size="sm"
                                    value={p.billingMode}
                                    onChange={(e) => handleUpdateParticipant(idx, 'billingMode', e.target.value)}
                                    disabled={saving || isLocked}
                                  >
                                    <option value="subscription">Scala da abbonamento</option>
                                    <option value="manual">Quota manuale</option>
                                  </Form.Select>
                                </td>
                                <td className="participant-col-details">
                                  <span className="mobile-col-label">Dettagli Quota</span>
                                  {p.billingMode === 'subscription' ? (
                                    <small className="text-muted d-block">
                                      {p.subscriptionName ? (
                                        <Badge bg="info" text="white" className="p-1 px-2 text-wrap text-start">
                                          {p.subscriptionName} {p.remainingEntries ? `(${p.remainingEntries} res)` : ''}
                                        </Badge>
                                      ) : (
                                        <span>Abbonamento attivo</span>
                                      )}
                                    </small>
                                  ) : (
                                    <div className="d-flex align-items-center gap-2">
                                      <div className="input-group input-group-sm" style={{ maxWidth: '120px' }}>
                                        <span className="input-group-text">€</span>
                                        <Form.Control
                                          type="number"
                                          size="sm"
                                          placeholder="0.00"
                                          value={p.price}
                                          onChange={(e) => handleUpdateParticipant(idx, 'price', e.target.value)}
                                          disabled={saving || isLocked}
                                        />
                                      </div>
                                      <Form.Check
                                        type="checkbox"
                                        id={`paid-check-${p.userId}`}
                                        label="Pagato"
                                        checked={p.isPaid}
                                        onChange={(e) => handleUpdateParticipant(idx, 'isPaid', e.target.checked)}
                                        disabled={saving || isLocked}
                                        className="small ms-1 mb-0"
                                      />
                                    </div>
                                  )}
                                </td>
                                <td className="participant-col-delete text-end">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="p-1 px-2"
                                    onClick={() => handleRemoveParticipant(idx)}
                                    disabled={saving || isLocked}
                                    title="Rimuovi partecipante"
                                    aria-label="Rimuovi partecipante"
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                <Form.Group className="mb-2" controlId="appointment-description">
                  <Form.Label className="fw-semibold small">Note / Descrizione</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Note opzionali per la lezione..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={saving || isLocked}
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="appointment-modal-footer">
            {isEditing && (
              <div className="appointment-footer-secondary">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="footer-btn"
                  onClick={() => handleDelete(false)}
                  disabled={saving || isLocked}
                >
                  <i className="fas fa-trash me-1"></i>
                  Elimina
                </Button>
                {seriesId && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="footer-btn"
                    onClick={() => handleDelete(true)}
                    disabled={saving || isLocked}
                  >
                    <i className="fas fa-calendar-times me-1"></i>
                    Elimina Serie
                  </Button>
                )}
                <Button
                  variant="success"
                  size="sm"
                  className="footer-btn"
                  onClick={() => setShowRollCallModal(true)}
                  disabled={saving}
                >
                  <i className="fas fa-clipboard-check me-1"></i>
                  Appello
                </Button>
              </div>
            )}
            <div className={`appointment-footer-primary ${!isEditing ? 'ms-auto' : ''}`}>
              <Button
                variant="secondary"
                size="sm"
                onClick={onHide}
                disabled={saving}
                className="footer-btn"
              >
                Annulla
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={saving || isLocked}
                className="footer-btn"
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-1"></i>
                    {isEditing ? 'Aggiorna' : 'Crea'}
                  </>
                )}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* RollCall Modal */}
      {showRollCallModal && (
        <AppointmentRollCallModal
          show={showRollCallModal}
          onHide={() => setShowRollCallModal(false)}
          appointment={appointmentDetail}
          onSuccess={() => {
            loadAppointment();
            if (onSuccess) onSuccess();
          }}
        />
      )}
    </>
  );
};

export default AppointmentModal;
